import type { Guide } from "../types";

// ── Modules ──────────────────────────────────────────────────────────────────
// The optional add-on modules that extend PulsePanels beyond everyday content:
// meeting-room and calendar displays (Event Management), full-screen emergency
// takeovers (Emergency Center), now-serving queues (Queue Management, run from
// queue.pulsepanels.com) and indoor wayfinding kiosks (Wayfinding). Queue and
// Wayfinding are paid modules that only appear when enabled for the organisation.
// Smart Ads / Campaign Manager and Fleet Management are hidden in the app
// (PR #184); their guides are kept below but should not be listed.

export const modulesGuides: Guide[] = [
  {
    slug: "smart-ads",
    category: "modules",
    route: "/modules/smart-ads",
    title: "Smart Ads & Campaign Manager",
    summary:
      "Run targeted advertising campaigns and manage advertisers, creatives, and schedules.",
    intro:
      "The Campaign Manager turns your screens into an advertising network. You create campaigns, register the advertisers behind them, upload their creatives (images and videos), then decide where and when each campaign plays. Once published, Smart Ads slot into your screens automatically on the schedule you set.",
    prerequisites: [
      "The Smart Ads module is enabled for your workspace.",
      "You have the creative files (images or videos) ready to upload.",
      "You have at least one screen to play the campaign on.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-07-28",
    steps: [
      {
        title: "Open the Campaign Manager",
        blocks: [
          {
            type: "p",
            text: "In the left sidebar, click Campaign Manager. (You can also reach it from Smart Ads under Modules.) This is the home for every advertising campaign in your workspace.",
          },
        ],
        screenshot: {
          src: "modules/smart-ads/01.png",
          alt: "The Campaign Manager page with the sidebar item highlighted",
          caption: "Campaign Manager — the hub for all your ad campaigns.",
          todo: "Campaign Manager page open, sidebar 'Campaign Manager' item highlighted, list of campaigns (or empty state) visible.",
        },
      },
      {
        title: "Create a campaign",
        blocks: [
          {
            type: "p",
            text: "Click New campaign, give it a clear name (for example “Summer Sale — Mall Screens”), and save. The campaign opens ready for you to add an advertiser and creatives.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Name campaigns by what they promote and where they run — it makes reporting far easier later.",
          },
        ],
        screenshot: {
          src: "modules/smart-ads/02.png",
          alt: "The new-campaign dialog with a name filled in",
          caption: "Name the campaign so it’s easy to find later.",
          todo: "New-campaign dialog open with a sample campaign name typed in.",
        },
      },
      {
        title: "Add an advertiser and upload creatives",
        blocks: [
          {
            type: "p",
            text: "Add the advertiser this campaign runs for, then upload their creatives — the images and videos that will actually appear on screen.",
          },
          {
            type: "ul",
            items: [
              "Advertiser — the brand or client paying for the campaign.",
              "Creative — an image or video ad. Add as many as the campaign needs.",
            ],
          },
        ],
        screenshot: {
          src: "modules/smart-ads/03.png",
          alt: "The advertiser and creative upload panel inside a campaign",
          caption: "Register the advertiser and upload their ad creatives.",
          todo: "Campaign editor showing an advertiser added and one or two uploaded creatives (image/video thumbnails).",
        },
      },
      {
        title: "Set targeting and schedule",
        blocks: [
          {
            type: "p",
            text: "Choose which screens the campaign targets and set its schedule — the start and end dates, and any time-of-day rules for when the ads should play.",
          },
          {
            type: "note",
            tone: "info",
            text: "Targeting controls where a campaign runs; the schedule controls when. A campaign only plays where both agree.",
          },
        ],
        screenshot: {
          src: "modules/smart-ads/04.png",
          alt: "The targeting and scheduling controls for a campaign",
          caption: "Pick the screens and the dates/times the campaign runs.",
          todo: "Targeting + schedule panel showing selected screens and a start/end date range.",
        },
      },
      {
        title: "Publish the campaign",
        blocks: [
          {
            type: "p",
            text: "When everything looks right, publish. The campaign goes live on the screens you chose and starts playing on schedule — no need to touch each screen individually.",
          },
        ],
        screenshot: {
          src: "modules/smart-ads/05.png",
          alt: "A published campaign shown as active in the Campaign Manager",
          caption: "Published — the campaign is now live on its target screens.",
          todo: "Campaign Manager list showing the campaign with an Active/Published status.",
        },
      },
    ],
    related: [
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Manage screens as a group", to: "/screens/screen-groups" },
    ],
    keywords: ["smart ads", "campaign", "advertising", "advertiser", "creative"],
  },

  {
    slug: "event-management",
    category: "modules",
    route: "/modules/event-management",
    title: "Event Management",
    summary:
      "Drive meeting-room door signs and lobby event boards from your Locations and Calendar Events.",
    intro:
      "Event Management powers the Meeting Room Display and Event Board apps. You create a Location for each room, link it to the screens that should show it, and fill its schedule — by adding events by hand or by syncing a Google Calendar. The door screen then shows whether the room is free or in use, and the lobby board lists what’s on across all your rooms.",
    prerequisites: [
      "You can see Modules in the sidebar and the Event Management card on it.",
      "You know which rooms (Locations) you want to show, and which screens stand at them.",
      "For automatic schedules, a Google account with access to the room’s calendar.",
    ],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the Event Management module",
        blocks: [
          {
            type: "p",
            text: "Click Modules in the sidebar, then click Open module on the Event Management card. The sidebar switches to the module and shows its two pages: Locations (your rooms) and Calendar Events (what’s booked in them). Click ‹ Modules at the top of the sidebar to go back.",
          },
        ],
        screenshot: {
          src: "modules/event-management/01.png",
          alt: "The Locations page, with the Event Management module sidebar showing Locations and Calendar Events",
          caption: "Event Management has two pages: Locations and Calendar Events.",
          todo: "Locations page (/meeting-locations) with the module sidebar (‹ Modules · Event Management · Locations · Calendar Events) and a few sample rooms.",
        },
      },
      {
        title: "Add a Location (meeting room)",
        blocks: [
          {
            type: "p",
            text: "On Locations, click Add Location. Fill in the General information card, then click Create Location.",
          },
          {
            type: "ul",
            items: [
              "Location Name — the room name shown on screen, for example “Boardroom A”.",
              "Building / Label and Floor Label — help you find the room in lists and on the Event Board.",
              "Timezone — the room’s local time, used for the clock and for event times.",
              "Event Display Window — how many days of events to show on screen (Today only up to 30 days).",
              "Wayfinding & icons — an optional Wayfinding Direction arrow (for example → Right) and Room icon, shown next to the room on the Event Board.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Keep names short and consistent, and include the building or floor if you have several sites — the Display preview on the right shows how the name fits on screen.",
          },
        ],
        screenshot: {
          src: "modules/event-management/02.png",
          alt: "The New Location form with General information and Wayfinding & icons cards",
          caption: "Name the room, set its timezone and how many days of events to show.",
          todo: "New Location form (/meeting-locations/new) with General information, Wayfinding & icons and the Display preview.",
        },
      },
      {
        title: "Link the room to its screens",
        blocks: [
          {
            type: "p",
            text: "Once the location is saved, open it again (the Edit location button on its row) and use the Screen Assignment card: search for the screen by the room’s door and pick it. A screen assigned here displays this location’s events. You can assign a room to more than one screen.",
          },
          {
            type: "p",
            text: "Back on Locations, the Assigned screens column shows how many screens each room is linked to. Use the row buttons to copy a location’s ID, view its events, or archive it (archiving keeps its events).",
          },
        ],
        screenshot: {
          src: "modules/event-management/03.png",
          alt: "The Locations list with one room showing an assigned screen",
          caption: "The Assigned screens column shows which rooms are linked to screens.",
          todo: "Locations list with at least one room showing 1 in the Assigned screens column.",
        },
      },
      {
        title: "Add events, or sync a Google Calendar",
        blocks: [
          {
            type: "p",
            text: "Open Calendar Events and click New Event. Choose how to add it:",
          },
          {
            type: "ul",
            items: [
              "Manual — pick the location and fill in Event Title, Start Time, End Time and the organizer. Tick Private event to hide the title on screens. PulsePanels warns you if the time clashes with another booking in the same room.",
              "Google Calendar — opens the Calendar for Google page. Click Connect Google Calendar and sign in (read-only access), then use Map a Calendar to link one of your calendars to a Meeting Room Location. Its events sync in automatically; use Sync All to pull changes straight away.",
            ],
          },
          {
            type: "p",
            text: "Outlook Calendar and Zoom Events are shown as Coming soon in this picker — to sync an Outlook calendar, connect it from Apps → Outlook Calendar instead (see the Outlook Calendar guide). Switch between the List, Day, Week and Month views, filter by location, status or source, and use Past Events or Delete All to tidy up old bookings.",
          },
          {
            type: "note",
            tone: "tip",
            text: "With a synced calendar you keep booking rooms in your normal calendar app — the screens follow on their own. Private meetings always appear as “Private Meeting”.",
          },
        ],
        screenshot: {
          src: "modules/event-management/04.png",
          alt: "The Calendar Events page listing room bookings with filters and the view switcher",
          caption: "Calendar Events lists every booking, whether typed in or synced.",
          todo: "Calendar Events page (/calendar-events) with a list of events, the filters and the List / Day / Week / Month switcher.",
        },
      },
      {
        title: "Show the schedule on screen",
        blocks: [
          {
            type: "p",
            text: "Give each linked screen one of the two event apps from Apps → App Store (Business):",
          },
          {
            type: "ul",
            items: [
              "Meeting Room Display — for the door screen: shows OCCUPIED or VACANT, the current meeting and what’s next for the room linked to that screen.",
              "Event Board — for lobbies and entrances: lists events across several rooms, with the direction arrows you set on each Location.",
            ],
          },
          {
            type: "steps-ref",
            label: "Set up the Meeting Room Display app",
            to: "/apps/meeting-room-display",
          },
          {
            type: "steps-ref",
            label: "Set up the Event Board app",
            to: "/apps/event-board",
          },
        ],
        screenshot: {
          src: "modules/event-management/05.png",
          alt: "The Meeting Room Display editor previewing a room as occupied, with its upcoming meetings",
          caption: "The door sign, previewed with the room’s live schedule.",
          todo: "A screen (or the app preview) running Meeting Room Display for a sample room such as “Boardroom A”, showing OCCUPIED/VACANT and the next booking.",
        },
      },
    ],
    related: [
      { label: "Meeting Room Display app", to: "/apps/meeting-room-display" },
      { label: "Event Board app", to: "/apps/event-board" },
      { label: "Calendar for Google app", to: "/apps/calendar-for-google" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: [
      "events",
      "meeting room",
      "calendar",
      "locations",
      "bookings",
      "google calendar",
      "occupied",
      "vacant",
      "room sign",
    ],
  },

  {
    slug: "fleet-management",
    category: "modules",
    route: "/modules/fleet-management",
    title: "Fleet Management",
    summary:
      "Track taxis and fleet vehicles, view the fleet, and review trip history.",
    intro:
      "Fleet Management keeps an eye on your taxis and vehicles from one place. The Overview gives you the big picture, the Fleet list shows every vehicle, and the Trip Log records where they’ve been. Pair it with the Taxi Stats app to put fleet numbers up on a screen.",
    prerequisites: [
      "The Fleet Management module is enabled for your workspace.",
      "Your vehicles are set up in the fleet so they can report trips.",
    ],
    readingTime: 3,
    status: "draft",
    lastUpdated: "2026-07-28",
    steps: [
      {
        title: "Open the Fleet Management overview",
        blocks: [
          {
            type: "p",
            text: "In the sidebar, open Modules → Fleet to land on the Overview. It summarises your fleet at a glance — how many vehicles you have and their current activity.",
          },
        ],
        screenshot: {
          src: "modules/fleet-management/01.png",
          alt: "The Fleet Management overview page",
          caption: "The Overview summarises your whole fleet.",
          todo: "Fleet overview page (/taxi) showing summary stats for the fleet.",
        },
      },
      {
        title: "View the fleet and vehicles",
        blocks: [
          {
            type: "p",
            text: "Open the Fleet list to see every vehicle, with its details and status. Use it to check which vehicles are active and to find a specific one.",
          },
        ],
        screenshot: {
          src: "modules/fleet-management/02.png",
          alt: "The fleet list showing individual vehicles",
          caption: "Every vehicle in your fleet, in one list.",
          todo: "Fleet/vehicles page (/taxi/fleet) listing individual vehicles with their status.",
        },
      },
      {
        title: "Read the trip log",
        blocks: [
          {
            type: "p",
            text: "Open the Trip Log to review recorded trips — a history of where your vehicles have been and when. It’s useful for auditing activity and spotting patterns.",
          },
        ],
        screenshot: {
          src: "modules/fleet-management/03.png",
          alt: "The Trip Log page listing recorded trips",
          caption: "The Trip Log records your fleet’s journeys.",
          todo: "Trip Log page (/taxi/trips) showing a table of recorded trips.",
        },
      },
      {
        title: "Show fleet stats on a screen",
        blocks: [
          {
            type: "p",
            text: "To display fleet numbers to an audience, use the Taxi Stats app — it puts live fleet figures on a screen as on-air signage.",
          },
          {
            type: "steps-ref",
            label: "Set up the Taxi Stats app",
            to: "/apps/taxi-stats-dashboard",
          },
        ],
      },
    ],
    related: [
      { label: "Taxi Stats app", to: "/apps/taxi-stats-dashboard" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: ["fleet", "taxi", "vehicles", "trips", "tracking"],
  },

  {
    slug: "emergency-center",
    category: "modules",
    route: "/modules/emergency-center",
    title: "Emergency Center",
    summary:
      "Send, schedule and stop full-screen emergency alerts on all or selected screens.",
    intro:
      "The Emergency Center is for the moments that matter most. An alert overrides whatever is playing and fills the screen with your message — on every screen, or only the screens, groups or locations you choose — until you stop it. Start from a ready-made template (fire, evacuation, security, medical, weather, maintenance), send an image or video, or write your own.",
    prerequisites: [
      "You are the organisation’s Owner or an Admin — only they can send, schedule or stop alerts. Auditors can open the page and read the history.",
      "Your screens are online, so they can pick the alert up.",
    ],
    readingTime: 4,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the Emergency Center",
        blocks: [
          {
            type: "p",
            text: "In the sidebar, click Emergency Center. The top strip shows Active incidents, Alerts sent today, Target screens, how sends are confirmed and your last activity. Below it, Send an alert lists the templates.",
          },
        ],
        screenshot: {
          src: "modules/emergency-center/01.png",
          alt: "The Emergency Center page with the stat strip and the Send an alert template grid",
          caption: "Emergency Center — templates, live status and history in one place.",
          todo: "Emergency Center page (/emergency) showing the hero, the stat strip and the eight template cards.",
        },
      },
      {
        title: "Pick a template and write the alert",
        blocks: [
          {
            type: "p",
            text: "Click a template card: Fire Emergency, Evacuation Alert, Security Alert, Medical Alert, Weather Alert, Maintenance Notice, Image / Video Alert or Create Custom Alert. Each card shows its priority (CRITICAL, HIGH or NORMAL), its language and whether it plays a sound.",
          },
          {
            type: "p",
            text: "In the alert window, edit the Title and Instructions (one per line) — Medical and Weather alerts also have a Message. For bilingual alerts, fill in the Arabic title and instructions (and message) too. You can also set the Emergency number shown in the footer, adjust the on-screen labels, and choose Language (English, Arabic or Bilingual), Sound and Flash.",
          },
          {
            type: "ul",
            items: [
              "Image / Video Alert — choose an image or video that fills the screen; the title and instructions become a caption.",
              "Save as template — keeps your wording as your organisation’s default for that template. Reset to default brings back the original.",
            ],
          },
          {
            type: "note",
            tone: "tip",
            text: "Keep it short: a title, one line of message and three or four instructions are readable from across a room.",
          },
        ],
        screenshot: {
          src: "modules/emergency-center/02.png",
          alt: "The alert window for a Fire Emergency with the title, instructions and Arabic fields filled in",
          caption: "Edit the wording, language, sound and flash before you send.",
          todo: "Click the Fire Emergency card: capture the alert window (Title, Instructions, Emergency number, Arabic fields, Language / Sound / Flash row).",
        },
      },
      {
        title: "Choose the screens and when to send",
        blocks: [
          {
            type: "p",
            text: "Lower in the same window, choose the target: All screens, or Select screens to tick individual screens (with search), whole Groups or Locations. Then choose Send now, or Schedule to pick a date and time — a scheduled alert can also auto-clear after a number of minutes.",
          },
        ],
        screenshot: {
          src: "modules/emergency-center/03.png",
          alt: "The target and schedule section of the alert window",
          caption: "Target all screens or a selection, now or at a set time.",
          todo: "Alert window scrolled to the target section with 'Select screens' chosen (Screens and Locations lists — a Groups list appears too when you have screen groups) and the Send now / Schedule options.",
        },
      },
      {
        title: "Confirm and send",
        blocks: [
          {
            type: "p",
            text: "Click Send now (or Schedule). The Confirm emergency dialog tells you exactly how many screens will be taken over; click Confirm & Send (or Confirm & Schedule). If your organisation requires a PIN, type the Emergency PIN first.",
          },
          {
            type: "p",
            text: "Screens switch to the alert within a few seconds. The alert then appears under Active emergencies with a live count of screens displayed, failed and offline.",
          },
          {
            type: "note",
            tone: "warning",
            text: "An alert takes over the targeted screens immediately and completely. Double-check the target before you confirm.",
          },
        ],
        screenshot: {
          src: "modules/emergency-center/04.png",
          alt: "The Confirm emergency dialog",
          caption: "The last check before the alert goes out.",
          todo: "Confirm emergency dialog ('Send “FIRE EMERGENCY”?… This will immediately take over N screens fullscreen') with Cancel and Confirm & Send.",
        },
      },
      {
        title: "Stop the alert",
        blocks: [
          {
            type: "p",
            text: "When the situation is over, click Stop next to the alert under Active emergencies, or Stop all to end every alert at once. A scheduled alert shows Cancel instead. Screens go back to their normal content automatically.",
          },
          {
            type: "note",
            tone: "info",
            text: "Screens stay on the alert until you stop it — unless you scheduled it with an auto-clear time.",
          },
        ],
        screenshot: {
          src: "modules/emergency-center/05.png",
          alt: "The Active emergencies list with Stop and Stop all buttons",
          caption: "Stop one alert, or Stop all to clear every screen.",
          todo: "Emergency Center with one live alert under Active emergencies (displayed/failed/offline counts) and the Stop / Stop all buttons.",
        },
      },
      {
        title: "Set confirmation, PIN and history",
        blocks: [
          {
            type: "p",
            text: "Click Settings at the top of the page to choose the Confirmation before sending: Confirmation dialog (default), Require PIN, or No confirmation (send instantly). Set or change the Emergency PIN here, and choose after how many days activity logs are deleted.",
          },
          {
            type: "p",
            text: "Click Emergency history to jump to Recent activity — every alert sent, scheduled or stopped, with who triggered it. Use Export CSV for your records.",
          },
          {
            type: "note",
            tone: "info",
            text: "If you try to delete a media file that an emergency template uses, the delete dialog lists the template under “Also used in”, so you can replace the image first.",
          },
        ],
        screenshot: {
          src: "modules/emergency-center/06.png",
          alt: "The Emergency settings dialog",
          caption: "Choose how sends are confirmed and how long logs are kept.",
          todo: "Emergency settings dialog (Confirmation before sending, Emergency PIN, Auto-delete activity logs).",
        },
      },
    ],
    related: [
      { label: "Manage screens as a group", to: "/screens/screen-groups" },
      { label: "View the Wall", to: "/screens/view-wall" },
      { label: "Upload media", to: "/content/upload-media" },
    ],
    keywords: [
      "emergency",
      "alert",
      "override",
      "broadcast",
      "takeover",
      "evacuation",
      "fire",
      "pin",
      "schedule alert",
    ],
  },

  {
    slug: "queue-management",
    category: "modules",
    route: "/modules/queue-management",
    title: "Queue Management",
    summary:
      "Run a now-serving queue: services, counters, operators, the ticket kiosk and the now-serving screen.",
    intro:
      "Queue Management runs a full ticketing system. Customers take a ticket at a kiosk, operators call the next number from their counter, and a Now Serving screen keeps everyone informed. Everything about the queue itself — services, counters, operators, the live console and reports — lives in the operator app at queue.pulsepanels.com. The customer-facing screens are ordinary PulsePanels apps.",
    prerequisites: [
      "Queue Management is enabled for your organisation. It is a paid module — if you don’t see the Queue apps in the App Store, ask your PulsePanels provider.",
      "A queue role: Queue Admins set up the queue; Operators run a counter.",
      "Screens for the Now Serving display and, if you use one, the ticket kiosk.",
    ],
    readingTime: 5,
    status: "draft",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Set up a queue system",
        blocks: [
          {
            type: "p",
            text: "Open queue.pulsepanels.com and sign in with your PulsePanels account (if you are already signed in to PulsePanels in the same browser, you go straight in). Open Settings and click + New queue system. Under General, set the Queue name, Timezone, Languages, SLA wait target, No-show timeout and Data retention, then click Save changes.",
          },
          {
            type: "ul",
            items: [
              "+ Add service — what customers queue for, for example “Payments”. Give it a ticket Prefix (A, B…), names in English, French and Arabic, a colour and an icon.",
              "+ Add counter — the positions operators work from, for example “Counter 3”. Tick the services each counter can serve.",
            ],
          },
          {
            type: "note",
            tone: "info",
            text: "Reset day (under Operations) closes all waiting tickets and restarts numbering at 1. Delete queue system permanently removes its services, counters, tickets and screen apps.",
          },
        ],
        screenshot: {
          src: "modules/queue-management/01.png",
          alt: "The queue Settings page with General, Services and Counters",
          caption: "Create the queue, then add its services and counters.",
          todo: "queue.pulsepanels.com → Settings with a sample queue: General card plus Services and Counters lists.",
        },
      },
      {
        title: "Invite your operators",
        blocks: [
          {
            type: "p",
            text: "Open Users and click + Invite operator. Operators are normal PulsePanels users: they get an email to set a password. Their queue role decides what they see — Admin is a Queue Admin, Editor is an Operator, Viewer is read-only.",
          },
        ],
        screenshot: {
          src: "modules/queue-management/02.png",
          alt: "The queue Users page with the Invite operator button",
          caption: "Invite the people who will run the counters.",
          todo: "queue.pulsepanels.com → Users page listing placeholder operators (e.g. Sara Ahmed, sara@example.com) and the + Invite operator button.",
        },
      },
      {
        title: "Operators pick a counter",
        blocks: [
          {
            type: "p",
            text: "When an operator opens queue.pulsepanels.com they see Select your counter. They choose a Free counter and click Start session. Supervisors can pick a busy counter and click Take over counter, which ends the other operator’s session. End session in the sidebar or top bar frees the counter again.",
          },
        ],
        screenshot: {
          src: "modules/queue-management/03.png",
          alt: "The Select your counter screen with free and busy counters",
          caption: "Operators claim a counter for their shift.",
          todo: "queue.pulsepanels.com → 'Select your counter' with a few counters (Free / In use) and the Start session button.",
        },
      },
      {
        title: "Call and serve the next ticket",
        blocks: [
          {
            type: "p",
            text: "On the Queue page, click Call next to call the next waiting ticket to your counter — it appears as the CURRENT TICKET and on the Now Serving screen. The buttons change with the ticket:",
          },
          {
            type: "ul",
            items: [
              "Called — Recall (call it again), Start service, No show, or Transfer to another service.",
              "Serving — Complete, Hold, or Transfer.",
              "On hold — Resume or Return it to the queue.",
            ],
          },
          {
            type: "p",
            text: "With no ticket in hand, Select ticket calls a specific number. The Waiting queue lists tickets under All, Standard and Priority; Recently served and the counters at the top (served today, average service, average wait, skipped) track your shift.",
          },
        ],
        screenshot: {
          src: "modules/queue-management/04.png",
          alt: "The operator console with the current ticket, Call next button and waiting queue",
          caption: "Call, serve and complete tickets from the console.",
          todo: "queue.pulsepanels.com → Queue page mid-shift: CURRENT TICKET, Call next, the action buttons, Waiting queue and Recently served.",
        },
      },
      {
        title: "Put the Now Serving screen and kiosk on your screens",
        blocks: [
          {
            type: "p",
            text: "Back in PulsePanels, open Apps → App Store and pick Queue / Now Serving (the board customers watch) or Queue Kiosk (the ticket dispenser customers tap). Choose the Queue it belongs to, adjust the look and calling voice, save it to My Apps and assign it to a screen like any other app.",
          },
          {
            type: "steps-ref",
            label: "Set up the Now Serving display",
            to: "/apps/queue-now-serving",
          },
          {
            type: "steps-ref",
            label: "Set up the Queue Kiosk",
            to: "/apps/queue-kiosk",
          },
        ],
        screenshot: {
          src: "modules/queue-management/05.png",
          alt: "The Queue Display editor with a live Now Serving preview",
          caption: "The Now Serving display keeps customers informed.",
          todo: "A screen (or the app preview) showing the Queue / Now Serving board with counter/number rows and next-up tickets.",
        },
      },
      {
        title: "Follow the day’s numbers",
        blocks: [
          {
            type: "p",
            text: "Overview shows each queue’s services and counters at a glance, and Tickets lists every ticket with its status and times. Supervisors and admins also get Analytics (waits, service times, SLA compliance, no-show rate) and Reports, which you can download with Export CSV.",
          },
        ],
      },
    ],
    related: [
      { label: "Now Serving display app", to: "/apps/queue-now-serving" },
      { label: "Queue Kiosk app", to: "/apps/queue-kiosk" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
    ],
    keywords: [
      "queue",
      "now serving",
      "counter",
      "ticket",
      "kiosk",
      "operator",
      "call next",
      "queue.pulsepanels.com",
    ],
  },

  {
    slug: "clinic-flow",
    category: "modules",
    route: "/modules/clinic-flow",
    title: "Clinic Flow: clinic door screens",
    summary:
      "Put each clinic's doctor, status and patient queue on the screen at its door, kept up to date by your hospital information system.",
    intro:
      "Clinic Flow drives the screen at each outpatient clinic's door. It shows the doctor and specialty, whether the clinic is available, in consultation, on break, in a procedure or closed, the ticket being served, the next patients and how late the clinic is running, in English, Arabic or both. Your hospital information system (HIS) keeps it up to date through the API; staff can also run a clinic from the Clinics page. Patients only ever appear as ticket numbers.",
    prerequisites: [
      "Clinic Flow is enabled for your organisation. It is a paid module: if the Clinic Flow card is missing from Modules, ask your PulsePanels provider.",
      "A screen at each clinic door.",
      "To connect your hospital system: the API & integrations add-on and an API key with the `clinics:write` scope.",
    ],
    readingTime: 6,
    status: "published",
    lastUpdated: "2026-10-07",
    steps: [
      {
        title: "Open the Clinics page",
        blocks: [
          {
            type: "p",
            text: "Click Modules in the sidebar and open Clinic Flow, then Clinics. The list on the left shows every clinic with its room number, doctor and live status; the panel on the right shows the selected clinic.",
          },
        ],
        screenshot: {
          src: "modules/clinic-flow/01.png",
          alt: "The Clinics page with the clinic list and the selected clinic's door preview",
          caption: "Every clinic and its door, live.",
          todo: "/clinics with 2-3 clinics in the list and one selected (door preview + Show on the door buttons).",
        },
      },
      {
        title: "Add your clinics",
        blocks: [
          {
            type: "p",
            text: "Click New clinic. Enter the Name (English) and Name (Arabic), the Room on the door (shown large on the screen, for example 12) and the Hospital system id: the id your HIS uses for this clinic room. Leave Time zone empty to use your organisation's.",
          },
          {
            type: "note",
            tone: "tip",
            text: "The Hospital system id is what links the clinic to your HIS: the system updates it as `/v1/clinics/ext:<id>`. If your HIS creates the clinics itself through the API, you don't need to add them here at all.",
          },
        ],
        screenshot: {
          src: "modules/clinic-flow/02.png",
          alt: "The New clinic dialog with names, room and hospital system id",
          todo: "New clinic dialog filled in: Sports Medicine Clinic / Arabic name / room 12 / OPD-12.",
        },
      },
      {
        title: "Put the Clinic Door app on each door screen",
        blocks: [
          {
            type: "p",
            text: "Each door screen runs the Clinic Door app, set to its clinic. Create one per clinic in Apps, then assign it to the screen at that door.",
          },
          { type: "steps-ref", label: "Set up the Clinic Door app", to: "/apps/clinic-door" },
        ],
      },
      {
        title: "Know what the door shows",
        blocks: [
          {
            type: "table",
            head: ["Status", "When", "The door shows"],
            rows: [
              ["Available", "A doctor's session is running and nobody is with the doctor", "The doctor, the next patients, and Please wait to be called"],
              ["In consultation", "A patient has been called or is with the doctor", "The ticket being served; a new call is highlighted with Please enter and a tone"],
              ["On break", "The session is on a break, or between two sessions on the same day", "Back at and the time"],
              ["Procedure in progress", "Set by staff or the HIS", "Please do not enter, and any note such as isolation precautions"],
              ["Closed", "No session now", "The next session, its day and time, and the doctor"],
            ],
          },
          {
            type: "p",
            text: "The footer shows the status, the session hours, how many patients are waiting, the average wait, and Running about N minutes late when the clinic is behind its appointment times.",
          },
        ],
        screenshot: {
          src: "modules/clinic-flow/03.png",
          alt: "A clinic door screen with the doctor, now serving ticket and next patients",
          caption: "In consultation: the ticket being served and the next patients.",
          todo: "/app-runtime/clinic-door?demo=1 at 1920x1080.",
        },
      },
      {
        title: "Show a procedure, a break or closed",
        blocks: [
          {
            type: "p",
            text: "Select the clinic and, under Show on the door, click Procedure in progress, On break or Closed. Choose How long (until you clear it, or for 15, 30 or 60 minutes) and add an optional Note, then click Show on the door. Click Clear to go back to what the schedule and patients give.",
          },
          {
            type: "note",
            tone: "info",
            text: "Your hospital system can set the same states through the API. A setting with a time limit ends by itself.",
          },
        ],
        screenshot: {
          src: "modules/clinic-flow/04.png",
          alt: "A clinic door showing Procedure in progress, Please do not enter",
          todo: "/app-runtime/clinic-door?demo=1&status=procedure at 1920x1080.",
        },
      },
      {
        title: "Run the queue from the Clinics page",
        blocks: [
          {
            type: "p",
            text: "Patients today lists the clinic's patients by ticket number. Most of it comes from your HIS, but staff can also work from here:",
          },
          {
            type: "ul",
            items: [
              "Call next finishes the patient with the doctor and calls the first waiting patient. If nobody is waiting, nothing changes.",
              "Call or Recall calls one patient (a recall is announced again). Arrived, Done and No-show update a patient's status.",
              "Add patient adds a walk-in by ticket number, with an optional appointment time. Never type a name.",
              "Add session adds a doctor's session for today (doctor and specialty in English and Arabic, from and to). Your HIS normally sends these.",
              "Fill with sample data adds a sample session and patients so you can show a door before the HIS is connected; Clear today removes today's patients.",
            ],
          },
        ],
        screenshot: {
          src: "modules/clinic-flow/05.png",
          alt: "Patients today with Call next, Add patient and per-patient actions",
          todo: "/clinics selected clinic scrolled to Patients today with sample data.",
        },
      },
      {
        title: "Connect your hospital information system",
        blocks: [
          {
            type: "p",
            text: "Create an API key with the `clinics:write` scope under Account Settings → Developer (ideally limited to your HIS's IP addresses) and give it to your HIS team or vendor with the Clinics API guide. The HIS then sends clinics, doctor sessions and visits, and the doors follow within a second.",
          },
          { type: "steps-ref", label: "Clinics API for hospital systems", to: "/developers/clinics-api" },
        ],
      },
      {
        title: "Patient privacy",
        blocks: [
          {
            type: "ul",
            items: [
              "A visit is a ticket number, its times and a status. There is nowhere to store a patient's name, record number, ID or diagnosis, and anything like that sent by a system is dropped before it is saved.",
              "Visits are deleted automatically after their clinic day. A clinic can keep them a few days longer if your hospital asks for it.",
              "Doctor sessions (names and specialties, no patient data) are kept for 30 days.",
            ],
          },
        ],
      },
    ],
    related: [
      { label: "Clinic Door app", to: "/apps/clinic-door" },
      { label: "Clinics API for hospital systems", to: "/developers/clinics-api" },
      { label: "Queue Management", to: "/modules/queue-management" },
    ],
    keywords: [
      "clinic",
      "clinic flow",
      "hospital",
      "his",
      "outpatient",
      "doctor",
      "patient",
      "now serving",
      "door screen",
      "medical",
      "healthcare",
      "consultation",
      "call next",
    ],
  },

  {
    slug: "wayfinding",
    category: "modules",
    route: "/modules/wayfinding",
    title: "Set up Wayfinding",
    summary:
      "Build a venue once — floors, floor plans, walkways, places and kiosks — then publish it to your touch screens.",
    intro:
      "Wayfinding turns a touch screen into a “you are here” kiosk that shows visitors the way to any place in your venue, step-free if they need it, in English and Arabic. You build each venue (a site) once in the Wayfinding module: a floor plan per floor, walkways drawn along the corridors, your places pinned to their doors, and a kiosk for every screen. When the checks pass, you publish, and every screen running the Wayfinding app follows.",
    prerequisites: [
      "Wayfinding is enabled for your organisation. It is a paid module — if the Wayfinding card is missing from Modules, ask your PulsePanels provider.",
      "A floor plan for each floor, as a PDF (an evacuation plan works) or an image.",
      "A list of the places visitors look for — or let the plan reader find them for you.",
    ],
    readingTime: 9,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Open Wayfinding and create a site",
        blocks: [
          {
            type: "p",
            text: "Click Modules in the sidebar, then Open module on the Wayfinding card. The Sites page lists your venues, each with its floors, places, kiosks and whether it is published. Click New site, type the Name (English) and Name (Arabic) — for example “Main Building” — and click Create site.",
          },
          {
            type: "note",
            tone: "tip",
            text: "New to Wayfinding? Inside a site, click Tour. “Set up a site” walks you through every step below, and “Draw walkways & lifts” shows every map tool. Both tours are also available in Arabic when PulsePanels is set to Arabic.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding/01.png",
          alt: "The Wayfinding Sites page with a site card and the New site button",
          caption: "Each site is one venue, built once and shared by all its screens.",
          todo: "Modules → Wayfinding (/wayfinding): Sites page with one sample site “Main Building” (floors · places · kiosks, status badge) and the New site button.",
        },
      },
      {
        title: "Add floors and their floor plans",
        blocks: [
          {
            type: "p",
            text: "Open the site’s Floors tab and click Add floor for each level. The short Label (G, 1, 2…) appears on the floor buttons; add the English and Arabic names; Order sets which floor sits above which. Then, on each floor:",
          },
          {
            type: "ul",
            items: [
              "Add floor plan — upload a PDF or image, or pick one from your media library. For a PDF with several pages, choose the page that is the plan.",
              "Set scale — click both ends of something you know the length of (a corridor, a door) and type its Real length (metres). Walking times and distances on the kiosk come from this.",
              "Replace plan — swap in a newer plan later; walkways and pins are rescaled to the new file.",
            ],
          },
        ],
        screenshot: {
          src: "modules/wayfinding/02.png",
          alt: "The Floors tab with a floor, its plan and the Set scale and Import from plan buttons",
          caption: "One floor per level, each with its own plan and scale.",
          todo: "Site → Floors tab with two floors (G, 1), plan thumbnails and the Replace plan / Set scale / Import from plan / Open map buttons.",
        },
      },
      {
        title: "Let the plan fill itself in",
        blocks: [
          {
            type: "p",
            text: "Click Import from plan on a floor, then Read the plan. The reader finds room labels, symbols (toilets, lifts, stairs, exits), painted walkways and new categories from the plan’s legend. It takes a minute or two. Review the list — untick anything wrong, fix names marked Low confidence — and click Add N places (N is the number ticked). Nothing is saved until you do.",
          },
          {
            type: "p",
            text: "When the plan changes later, the same button reads Update from plan. It compares the new plan with what’s already on the floor and lists only what changed: new places, moved or renamed ones, and ones not found on the new plan. Tick what should change and click Apply N changes. Nothing is removed — places missing from the new plan can only be hidden, and you can show them again from the Places tab.",
          },
          {
            type: "note",
            tone: "info",
            text: "If the new plan is shifted or scaled, the reader lines it up using rooms found on both plans. If it can’t, use Line up by hand… and click two places on the new plan.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding/03.png",
          alt: "The Import from plan window for a floor, with the Read the plan button",
          caption: "Read the plan, then review everything it found before it is added.",
          todo: "Currently shows the window BEFORE reading (the local demo has no plan reader: Python + Tesseract). Retake: Import from plan — <floor> window after 'Read the plan': the plan with coloured markers (Place, Facility, Lift / stairs, Walkway) and the review list with Add N places.",
        },
      },
      {
        title: "Draw walkways, lifts and stairs",
        blocks: [
          {
            type: "p",
            text: "Open the Map tab and pick a floor. Routes follow walkways — lines you draw down the middle of each corridor, all joined into one network. The toolbar has a key for every tool:",
          },
          {
            type: "table",
            head: ["Tool", "Key", "What it does"],
            rows: [
              ["Select & move", "V", "Drag points, pins and kiosks; click a line to mark it Step-free or Closed"],
              ["Pan", "H", "Move around the plan (or hold Space in any tool)"],
              ["Draw walkways", "W", "Click along a corridor; Esc finishes the line; hold Shift for any angle"],
              ["Lift / stairs", "L", "Turn a walkway point into a Lift, Stairs, Escalator or Ramp"],
              ["Place destinations", "D", "Pin places to their doors"],
              ["Add kiosk", "K", "Mark where a screen stands"],
              ["Erase", "E", "Remove a point, line, pin or kiosk"],
            ],
          },
          {
            type: "ul",
            items: [
              "Start or end a line on an existing point or line to join it. A walkway that touches nothing is an island, and nothing on it can be reached.",
              "Give each lift or staircase a Link name and use exactly the same name on every floor it serves (for example “Lift – North”) — that’s what lets a route change floors. Tick Staff only for service lifts.",
              "Undo with Ctrl+Z and redo with Ctrl+Y. Everything you draw saves by itself.",
            ],
          },
        ],
        screenshot: {
          src: "modules/wayfinding/04.png",
          alt: "The Map tab with walkways drawn along corridors and a lift linking floors",
          caption: "Walkways along the corridors, joined into one network.",
          todo: "Site → Map tab: a floor plan with walkways drawn, a lift point selected (Lift / stairs panel with Link name), the tool bar and the hint line visible.",
        },
      },
      {
        title: "Pin places to their doors",
        blocks: [
          {
            type: "p",
            text: "Choose the Place destinations tool (D). Pick a place from the list and click its door on the plan — the next unplaced place is picked for you. A pin joins the nearest walkway by itself, so put it at the door, next to a walkway.",
          },
          {
            type: "p",
            text: "A room that isn’t a place yet? With New place picked, click its door, type its name (and Arabic name and category if you like) and click Add place — it is added and pinned in one go.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding/05.png",
          alt: "The Place destinations panel with unplaced places and pins on the plan",
          caption: "Pick a place, click its door — done.",
          todo: "Map tab with the Place destinations tool active: the list of places not on the map yet and a few pins on the plan.",
        },
      },
      {
        title: "Tidy up places and categories",
        blocks: [
          {
            type: "p",
            text: "The Places tab lists every destination with its English and Arabic names, category, floor and status — Open, Closed (shown, marked closed) or Hidden. Edit a cell and it saves. Tick rows to Set category, Move to floor or Set status in bulk. The pencil opens details visitors see: description, opening hours, phone, logo and search keywords. Long lists are split into pages.",
          },
          {
            type: "p",
            text: "To work in a spreadsheet, click Template, fill it in and use Import CSV (add every row as new, or update matching places); Export downloads what you have. On the Categories tab, click New category and choose its icon and colour — categories are the tiles visitors browse on the kiosk, and their colour is used for the map pins.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding/06.png",
          alt: "The Places tab with a table of places and bulk actions",
          caption: "Every place in one table — edit in place or round-trip a spreadsheet.",
          todo: "Site → Places tab with sample places (e.g. Reception, Prayer Room, Lab 101), filters, Import CSV / Export / Template / Add place, and the pager.",
        },
      },
      {
        title: "Add a kiosk for every screen",
        blocks: [
          {
            type: "p",
            text: "A kiosk is where a screen stands: the “You are here” point every route starts from. On the Map tab, choose Add kiosk (K), click where the screen stands, then drag its arrow the way the screen faces — so “turn left” really means left for the visitor.",
          },
          {
            type: "p",
            text: "Then open the Kiosks tab and choose the Screen that stands at each kiosk. The list offers only screens in the site’s Space (or in no Space). A screen can only stand at one kiosk: if it already stands at a kiosk of another site, you are asked “Take this screen from another site?” — Link it here moves it (it stops routing from the other site at once), Cancel leaves it where it is. In Screens → Wall View, the screen’s Settings also show its Wayfinding position.",
          },
          {
            type: "note",
            tone: "info",
            text: "A screen linked to a kiosk that is already published uses it right away. A new kiosk reaches its screen when you publish; until then that screen uses its app’s fallback kiosk, if it has one.",
          },
          {
            type: "p",
            text: "Deleting a kiosk — from the Kiosks tab, the Erase tool, the Delete key or its details — always asks “Delete kiosk “<name>”?” first. If a screen stands there, it is unlinked right away and, until you link it to another kiosk, only shows routes if its app has a fallback kiosk.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding/07.png",
          alt: "The Kiosks tab with the note about when a kiosk reaches its screen, and a screen chosen for each kiosk",
          caption: "Link each kiosk to the screen that stands there.",
          todo: "Site → Kiosks tab: the 'A screen linked to a kiosk that is already published…' note and the kiosk list (Kiosk, Floor, Faces, Screen dropdown) with placeholder screens such as “Lobby Screen”.",
        },
      },
      {
        title: "Run the checks and fill in Arabic names",
        blocks: [
          {
            type: "p",
            text: "The Checks button at the top runs the kiosks’ own routing on your draft: places that can’t be reached, walkways that aren’t joined, lifts not linked to another floor, missing Arabic names. Show takes you straight to each problem. When everything is fine the button reads All checks pass.",
          },
          {
            type: "p",
            text: "If names are missing their Arabic, click Fill Arabic names. Numbers and room codes are copied as they are and names are translated as suggestions; check each one, fix what’s wrong, and click Save to keep the ticked rows.",
          },
          {
            type: "p",
            text: "To see a route exactly as a screen will draw it, use Try a route on the Map tab: pick a kiosk and a destination, and tick Step-free to test the wheelchair route.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding/08.png",
          alt: "The Map checks window listing issues with Show buttons",
          caption: "Checks find unreachable places and loose walkways before visitors do.",
          todo: "Map checks window with a couple of warnings and the 'N names have no Arabic' banner with Fill Arabic names.",
        },
      },
      {
        title: "Publish to your screens",
        blocks: [
          {
            type: "p",
            text: "Nothing reaches the screens until you publish. Click Publish, then Publish again in Publish to screens? — every screen showing this site switches to the new version within about a minute. Errors must be fixed first; warnings can be published.",
          },
          {
            type: "ul",
            items: [
              "Settings tab — the site names, the Kiosk default language, the Phone link visitors scan to take a route with them (New link makes a fresh one), and Publish history, where Restore puts an earlier version back on the screens.",
              "Analytics tab — visitor sessions, searches, routes shown (with how many couldn’t be shown), routes taken to a phone, the most requested places, Couldn’t show a route, and searches with no results (add those words as search keywords on the right place). Each route counts once per visit to a place. Export downloads it all as a CSV, including a “route not shown” row per place.",
              "Couldn’t show a route lists places visitors opened when the kiosk had no route to show, with the reason: The screen has no kiosk on the map, The place is not on the map, No walkway reaches it, or No step-free way there. Fix the map (Show, or Kiosks), then publish.",
            ],
          },
        ],
        screenshot: {
          src: "modules/wayfinding/09.png",
          alt: "The Publish to screens confirmation",
          caption: "Publish when the checks pass — screens update within a minute.",
          todo: "Site header with the Publish button and the 'Publish to screens?' dialog open.",
        },
      },
    ],
    related: [
      { label: "Show Wayfinding on a kiosk", to: "/modules/wayfinding-kiosk" },
      { label: "View the Wall", to: "/screens/view-wall" },
      { label: "Upload media", to: "/content/upload-media" },
    ],
    keywords: [
      "wayfinding",
      "floor plan",
      "map",
      "walkways",
      "lifts",
      "stairs",
      "kiosk",
      "you are here",
      "directions",
      "step-free",
      "arabic",
      "venue",
      "site",
    ],
  },

  {
    slug: "wayfinding-kiosk",
    category: "modules",
    route: "/modules/wayfinding-kiosk",
    title: "Show Wayfinding on a kiosk",
    summary:
      "Put a published Wayfinding site on a touch screen with the Wayfinding app — map, search, branding and signage.",
    intro:
      "The Wayfinding app is what visitors actually touch. While nobody is using it, it shows just a search field — “Where do you want to go?” — over your map, with optional signage playing underneath or a full-screen screen saver. Visitors search or browse, see the route drawn from where they stand, and can scan a QR code to take it with them. One app can serve every screen in a site: each screen starts its routes from its own kiosk.",
    prerequisites: [
      "A Wayfinding site that has been published at least once.",
      "Your screens placed as kiosks in the site (Kiosks tab), so each one knows where it stands.",
      "A touch screen, portrait or landscape.",
    ],
    readingTime: 6,
    status: "draft",
    lastUpdated: "2026-10-01",
    steps: [
      {
        title: "Create the Wayfinding app",
        blocks: [
          {
            type: "p",
            text: "Open Apps → App Store and pick Wayfinding (under Business). In the editor, give the app a name (for example “Lobby wayfinding”) and choose the Site. The editor tells you which published version the screens will show, and warns you if the site has never been published.",
          },
          {
            type: "p",
            text: "Choose a Fallback kiosk for screens that aren’t placed on the map (or whose new kiosk isn’t published yet) — or None to show places without a route. Under Popular places, add up to eight places that get big buttons on the home screen.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding-kiosk/01.png",
          alt: "The Wayfinding app editor with a site chosen and a live preview",
          caption: "Pick the site, the fallback kiosk and your popular places.",
          todo: "Apps → App Store → Wayfinding (/apps/edit/new?type=WAYFINDING): editor with Site “Main Building”, Fallback kiosk and Popular places, live preview on the right.",
        },
      },
      {
        title: "Set up the map",
        blocks: [
          {
            type: "ul",
            items: [
              "Map rotation — Turn the map to match where the screen faces (recommended), or Keep the floor plan as drawn. Use the buttons and slider to fine-tune. Directions (left / right) always follow where the screen really faces.",
              "Place dots on the map — Only for the chosen category or search (recommended), or Always.",
              "Map zoom — let visitors pinch, drag and zoom, and choose when the map goes back to the full view.",
              "Browse subtitle — the line next to Browse, in English and Arabic.",
            ],
          },
        ],
        screenshot: {
          src: "modules/wayfinding-kiosk/02.png",
          alt: "Map rotation, place dots and map zoom settings",
          caption: "Turn the map so “up” is the way the visitor is facing.",
          todo: "Editor scrolled to Map rotation, Browse subtitle, Place dots on the map and Map zoom.",
        },
      },
      {
        title: "Choose language and behaviour",
        blocks: [
          {
            type: "ul",
            items: [
              "Language — One at a time, with an EN / ع switch, or English and Arabic together. Starts in picks the first language (or the site default).",
              "Numbers — Latin (0 1 2 3) or Arabic-Indic (٠ ١ ٢ ٣) digits.",
              "Back to start after — how long without a touch before the kiosk clears the route and returns home.",
              "On screen — show the Step-free route button, and switch to the Dark theme.",
            ],
          },
        ],
      },
      {
        title: "Brand the top bar and add an info button",
        blocks: [
          {
            type: "p",
            text: "Set the Accent colour and Logo, then choose the Top bar title (Logo and venue name, Logo only (larger), or Venue name only) and the Clock. Top bar size lets you set the logo height and width and the bar height as a percentage of the screen.",
          },
          {
            type: "p",
            text: "Tick Show an info button to add an “i” next to the clock. It opens a popup with your own title and text in both languages — opening hours, reception, lost & found — and, optionally, your Wi-Fi: enter the network name and password and visitors scan a QR code to join.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding-kiosk/03.png",
          alt: "The kiosk preview with the info popup and Wi-Fi QR code open",
          caption: "An info popup with a Wi-Fi QR code visitors can scan.",
          todo: "Editor preview with the “i” popup open (Visitor information text + Wi-Fi QR). Use a placeholder network name such as “Guest-WiFi”.",
        },
      },
      {
        title: "Play signage while the kiosk is idle",
        blocks: [
          {
            type: "p",
            text: "Content under the map plays a playlist in the space below the map while nobody is using the kiosk; choose how it fits and when search and browse fold away again. Images and videos play; apps and documents in the playlist are skipped. The editor shows the exact pixel size to design for.",
          },
          {
            type: "p",
            text: "For a full-screen screen saver instead, tick Show a screen saver when idle. Choose a playlist or a single image / video, when it starts, and a headline in English and Arabic — visitors see “Touch to find your way”.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding-kiosk/04.png",
          alt: "The kiosk preview with signage playing under the map",
          caption: "Signage plays under the map until someone touches the screen.",
          todo: "Editor preview (Kiosk view) with a playlist playing under the map and the idle search field on top.",
        },
      },
      {
        title: "Preview, save and assign to screens",
        blocks: [
          {
            type: "p",
            text: "The live preview works like the real kiosk — tap around. Switch between Kiosk and Screen saver, Landscape and Portrait, and choose which kiosk to preview from. Click Save Changes; the app appears in My Apps. Assign it to each wayfinding screen like any other app — every screen routes from the kiosk it is linked to.",
          },
          {
            type: "steps-ref",
            label: "How to assign content to a screen",
            to: "/screens/assign-content",
          },
          {
            type: "note",
            tone: "info",
            text: "Changes to the site itself (places, walkways, names) reach the screens only when you publish the site. Changes to this app reach the screens when you save.",
          },
          {
            type: "p",
            text: "When a visitor opens a place that isn’t pinned on the map yet, the kiosk says “This place isn't on the map yet. Ask at reception for directions.” instead of a route. In Arabic, directions name lifts and stairs in Arabic too.",
          },
        ],
        screenshot: {
          src: "modules/wayfinding-kiosk/05.png",
          alt: "A wayfinding kiosk showing a route from You are here to a destination",
          caption: "Visitors see the route from exactly where they stand.",
          todo: "Preview (or a real screen) showing a route drawn from “You are here” to a sample place, with the step-free toggle and the 'Take this route with you' QR.",
        },
      },
    ],
    related: [
      { label: "Set up Wayfinding", to: "/modules/wayfinding" },
      { label: "Assign content to a screen", to: "/screens/assign-content" },
      { label: "Build a playlist", to: "/content/build-a-playlist" },
    ],
    keywords: [
      "wayfinding",
      "kiosk",
      "touch screen",
      "map",
      "directions",
      "screen saver",
      "wi-fi",
      "qr",
      "arabic",
      "step-free",
    ],
  },
];
